/**
 * Bildschirm „Zugriffsverwaltung" (`screen-access`, Spec 05a, „Bildschirm
 * Zugriffsverwaltung"). Erreichbar über das Konto-Menü (`account-access`,
 * `router.tsx::AccountMenu`) unter `/zugriff` — kein siebter Bereich in der
 * Symbolleiste (ADR 0011), die Route hängt an keinem `rail-*`-Eintrag.
 *
 * Eine Tabelle am Schreibtisch, Karten unter 768px — eine Zeilenform mit
 * responsiven Tailwind-Klassen statt zweier Render-Pfade (dasselbe Muster wie
 * `features/contracts/ContractList.tsx::ContractRow`), nicht der
 * `useIsDesktopViewport`-Umbau, den `TenantLedger` für einen wechselnden
 * ⋯-Auslöser braucht — hier ändert sich nur die Anordnung, kein Bedienelement.
 *
 * Seit Spec 05b führt `access-user-row-<id>` auf `screen-access-user`
 * (`routes/AccessUser.tsx`) — vorher ein `<li>` ohne Ziel. Die Art einer
 * Person kommt seither wörtlich aus `person_kind.label`, nicht mehr aus dem
 * inzwischen entfernten `features/access/format.ts::personKindLabel`
 * (AK-382).
 *
 * Spec 25, Oberfläche B: Spalte *Status* (Vollzugriff/Gesperrt-Kennsätze)
 * zwischen *Zugriffe* und *Angelegt* (vorher „Eingeladen"). Die primäre
 * Aktion „Person anlegen" (Bezeichner `access-invite` bleibt, R6) erscheint
 * **nur für den Administrator** — `is_admin` aus `GET /access/me` (Bequemlichkeit,
 * kein Schutz; der Schutz ist `require_admin` am Server).
 */

import { Link } from '@tanstack/react-router'
import { useState } from 'react'

import { EmptyState } from '@/components/states/EmptyState'
import { ErrorState } from '@/components/states/ErrorState'
import { SkeletonRows } from '@/components/states/Skeleton'
import { formatInstantInBerlin } from '@/lib/date'
import { useAccessUsers, useMe } from '@/features/access/api'
import type { PersonListItem } from '@/features/access/api'
import { AccessInviteSheet } from '@/features/access/AccessInviteSheet'
import { DisabledBadge, FullAccessBadge } from '@/features/access/PersonBadges'

export function Access() {
  const usersQuery = useAccessUsers()
  const meQuery = useMe()
  const isAdmin = meQuery.data?.is_admin === true
  const [isInviteOpen, setIsInviteOpen] = useState(false)

  return (
    <section aria-labelledby="access-heading" data-testid="screen-access">
      <header className="border-border flex min-h-(--row-h) items-center justify-between gap-3 border-b px-(--page-x) py-1">
        <h1 id="access-heading" className="text-page font-(--font-weight-page)">
          Zugriffsverwaltung
        </h1>
        {/* Nur, wenn die Liste schon Personen zeigt — sonst stehen zwei
         * akzentgefüllte „Person anlegen" gleichzeitig da (K8/K-D4,
         * `design-reviewer`): dieser Knopf hier und die Aufforderung des
         * Leerzustands unten. Echte Daten sind hier nie leer (man selbst
         * steht immer in der Liste, AK-354-Kommentar im Journey-Test) — die
         * Bedingung greift nur im erzwungenen Leerzustand des Tests und bei
         * Laden/Fehler. Dasselbe Muster wie `ContractList.tsx`. */}
        {isAdmin && usersQuery.isSuccess && usersQuery.data.items.length > 0 && (
          <button
            type="button"
            data-testid="access-invite"
            onClick={() => {
              setIsInviteOpen(true)
            }}
            className="bg-accent rounded-card text-body px-4 text-accent-on transition-opacity duration-(--duration-fast) ease-(--ease-out)"
          >
            Person anlegen
          </button>
        )}
      </header>

      {usersQuery.isPending && <SkeletonRows testId="access-users-loading" rows={6} />}

      {usersQuery.isError && (
        <ErrorState
          testId="access-users-error"
          retryTestId="access-users-retry"
          error={usersQuery.error}
          onRetry={() => {
            void usersQuery.refetch()
          }}
        />
      )}

      {usersQuery.isSuccess && usersQuery.data.items.length === 0 && (
        <EmptyState
          testId="access-users-empty"
          title="Außer dir hat noch niemand Zugriff."
          action={
            isAdmin && (
              <button
                type="button"
                data-testid="access-users-invite"
                onClick={() => {
                  setIsInviteOpen(true)
                }}
                className="bg-accent rounded-card text-body px-4 text-accent-on transition-opacity duration-(--duration-fast) ease-(--ease-out)"
              >
                Person anlegen
              </button>
            )
          }
        />
      )}

      {usersQuery.isSuccess && usersQuery.data.items.length > 0 && (
        <>
          {/* Spaltenköpfe nur am Schreibtisch — bei 380px ersetzt die
           * Kartenform die Tabelle vollständig (Spec 05a, „Schmale Ansicht"). */}
          <div className="text-label text-muted border-border hidden border-b px-(--page-x) py-1 md:flex md:gap-3">
            <span className="flex-1">Name</span>
            <span className="w-20">Art</span>
            <span className="w-24 text-right">Zugriffe</span>
            <span className="w-40">Status</span>
            <span className="w-32 text-right">Angelegt</span>
          </div>
          <ul data-testid="access-users">
            {usersQuery.data.items.map((person) => (
              <PersonRow key={person.id} person={person} />
            ))}
          </ul>
        </>
      )}

      <AccessInviteSheet open={isInviteOpen} onOpenChange={setIsInviteOpen} />
    </section>
  )
}

function PersonRow({ person }: { readonly person: PersonListItem }) {
  // `invited_at` ist ein `datetime`, kein reines Datum — `formatInstantInBerlin`
  // rechnet in die Zeitzone um, statt die UTC-Ziffern der Zeichenkette wörtlich
  // zu lesen (A10, `design-reviewer`: nahe der Tagesgrenze zeigte das bisher
  // den falschen Tag, `lib/date.ts` führt die Zeitzone bereits für `heute`).
  const invitedLabel = person.invited_at === null ? '—' : formatInstantInBerlin(person.invited_at)
  // Spec 25, Oberfläche B: „dann ist die ganze Zeile gedämpft (`text-muted`),
  // nicht ausgeblendet" — dieselbe Regel wie abgelaufene Grants in 05a, hier
  // aber wörtlich über `text-muted` statt `opacity-60` (Spec-Wortlaut).
  const isDisabled = person.state.value === 'disabled'
  const nameClass = isDisabled ? 'text-muted' : 'text-text'

  return (
    <li className="border-border border-b last:border-b-0">
      <Link
        to="/zugriff/$userId"
        params={{ userId: person.id }}
        data-testid={`access-user-row-${person.id}`}
        className="hover:bg-bg flex flex-col gap-1 px-(--page-x) py-2 transition-colors duration-(--duration-fast) ease-(--ease-out) md:flex-row md:items-center md:gap-3 md:py-1.5"
      >
        <span className="flex min-w-0 flex-col md:flex-1">
          <span className={`text-body truncate font-medium ${nameClass}`}>{person.display_name ?? person.email}</span>
          <span className="text-label text-muted truncate">{person.email}</span>
        </span>

        {/* Unter 768px eine Zeile „Extern · 2 Zugriffe · Vollzugriff ·
         * Gesperrt · angelegt am …" statt der Spalten (Spec 25, „Schmale
         * Ansicht") — nur die gesetzten Teile, Trenner „ · ". */}
        <span className="text-label text-muted md:hidden">
          {[
            person.person_kind.label,
            `${String(person.grant_count)} ${person.grant_count === 1 ? 'Zugriff' : 'Zugriffe'}`,
            person.full_access ? 'Vollzugriff' : null,
            isDisabled ? person.state.label : null,
            person.invited_at !== null ? `angelegt am ${invitedLabel}` : null,
          ]
            .filter((part) => part !== null)
            .join(' · ')}
        </span>

        <span className={`text-body hidden md:block md:w-20 ${nameClass}`}>{person.person_kind.label}</span>
        <span data-numeric className={`text-body hidden md:block md:w-24 md:text-right ${nameClass}`}>
          {person.grant_count}
        </span>
        <span className="hidden items-center gap-1 md:flex md:w-40">
          {person.full_access && <FullAccessBadge testId={`access-user-full-access-${person.id}`} />}
          {isDisabled && <DisabledBadge testId={`access-user-disabled-${person.id}`} label={person.state.label} />}
        </span>
        <span className="text-label text-muted hidden md:block md:w-32 md:text-right">{invitedLabel}</span>
      </Link>
    </li>
  )
}
